import type {RecipeEntry, RecipeId} from "../../models/GroupRepo";
import type {CookStats} from "../../models/recipe-queries";

export type RecipeOrder = "name" | "lastCooked" | "countLastYear" | "countTotal";
// `compact` hides the section headings
export type RecipeView = {order: RecipeOrder; compact: boolean};
export const defaultRecipeView: RecipeView = {order: "name", compact: true};

export type RecipeHeading =
  | {kind: "letter"; letter: string}
  | {kind: "month"; year: number; month: number}
  | {kind: "year"; year: number}
  | {kind: "never"}
  // count buckets are labelled by their lower bound: 0, 1, 2+, 5+, ...
  | {kind: "count"; min: number};

export type RecipeSection = {key: string; heading: RecipeHeading; recipes: RecipeEntry[]};

const countBuckets = [100, 50, 25, 10, 5, 2, 1, 0];
// older than this many months, last-cooked sections cover a whole year
const monthSectionsFor = 18;

// "Äpfel" goes under A, where `localeCompare` sorts it, and names starting with a digit or emoji
// share one section
const getLetter = (name: string) => {
  const first = name.trim().normalize("NFD").charAt(0).toLocaleUpperCase();
  return /\p{L}/u.test(first) ? first : "#";
};

const getLastCookedHeading = (date: Date | null, now: Date): RecipeHeading => {
  if (!date) return {kind: "never"};
  const year = date.getFullYear();
  const month = date.getMonth();
  const monthsAgo = (now.getFullYear() - year) * 12 + now.getMonth() - month;
  return monthsAgo > monthSectionsFor ? {kind: "year", year} : {kind: "month", year, month};
};

const getHeadingKey = (heading: RecipeHeading) => {
  switch (heading.kind) {
    case "letter":
      return `letter:${heading.letter}`;
    case "month":
      return `month:${heading.year}-${heading.month}`;
    case "year":
      return `year:${heading.year}`;
    case "never":
      return "never";
    case "count":
      return `count:${heading.min}`;
  }
};

/**
 * Sorts recipes that come in sorted by name and splits them into sections. Sorting is stable, so
 * recipes that tie stay in name order.
 */
export const orderRecipes = (
  recipes: RecipeEntry[],
  order: RecipeOrder,
  statsById: Map<RecipeId, CookStats>,
  now: Date
): RecipeSection[] => {
  const getStats = (recipe: RecipeEntry) => statsById.get(recipe.id);
  let sorted: RecipeEntry[];
  let getHeading: (recipe: RecipeEntry) => RecipeHeading;
  if (order === "name") {
    sorted = recipes;
    getHeading = (recipe) => ({kind: "letter", letter: getLetter(recipe.name)});
  } else if (order === "lastCooked") {
    const getTime = (recipe: RecipeEntry) => getStats(recipe)?.lastCookedAt?.getTime() ?? 0;
    sorted = [...recipes].sort((a, b) => getTime(b) - getTime(a));
    getHeading = (recipe) => getLastCookedHeading(getStats(recipe)?.lastCookedAt ?? null, now);
  } else {
    const getCount = (recipe: RecipeEntry) =>
      (order === "countLastYear" ? getStats(recipe)?.lastYear : getStats(recipe)?.total) ?? 0;
    sorted = [...recipes].sort((a, b) => getCount(b) - getCount(a));
    getHeading = (recipe) => ({
      kind: "count",
      min: countBuckets.find((min) => getCount(recipe) >= min)!,
    });
  }

  const sections: RecipeSection[] = [];
  for (const recipe of sorted) {
    const heading = getHeading(recipe);
    const key = getHeadingKey(heading);
    const last = sections.at(-1);
    if (last?.key === key) {
      last.recipes.push(recipe);
    } else {
      sections.push({key, heading, recipes: [recipe]});
    }
  }
  return sections;
};
